import Navbar from 'react-bootstrap/Navbar';
import Container from 'react-bootstrap/Container';
import Nav from 'react-bootstrap/Nav';
import { AwpDropdownMenuItem, AwpMenuItem, AwpNavDropdown } from '@components/AwpMenuItem';
import { EnabledFeature, ENTITY_TITLES, EventTypes, FORM_MODE, Permissions, Views } from '@awp/constants';
import { logout } from '@awp/keycloak';
import { hasAccess } from '@components/Authorize';
import Dropdown from 'react-bootstrap/Dropdown';
import SupportOffline from '@components/SupportOffline';
import React from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '@stores/usersSlice';

const HeaderMenu = (props: any) => {
  const { expandedClick, isBarExpanded = false } = props;
  const currentUser = useSelector(selectCurrentUser);

  return (
    <Navbar
      variant="light"
      bg="light"
      expanded={isBarExpanded}
      expand="lg"
      className={`main-nav awp-header-menu border-top ${isBarExpanded ? 'border-bottom' : ''}`}
    >
      <Container>
        <Navbar.Collapse id="responsive-navbar-nav">
          <Nav className="me-auto">
            <AwpMenuItem
              {...props}
              to="/"
              title="Dashboard"
              permissions={[Permissions.ReadUserDashboard]}
              supportOffline={true}
              onClick={() => expandedClick()}
            />
            <AwpNavDropdown
              id="navbarDropdownUser"
              title={currentUser.UserName}
              supportOffline={false}
              className="d-block d-lg-none"
            >
              <AwpDropdownMenuItem
                {...props}
                title="My Profile"
                to={Views.MyProfile}
                permissions={[Permissions.ReadUserDashboard]}
                onClick={() => expandedClick()}
              />
              <AwpDropdownMenuItem
                {...props}
                title="My Preferences"
                to={Views.UserPreferences}
                permissions={[Permissions.ReadUserDashboard]}
                onClick={() => expandedClick()}
              />
              <AwpDropdownMenuItem
                {...props}
                to="/"
                title="Sign out"
                permissions={[Permissions.ReadUserDashboard]}
                onClick={logout}
              />
            </AwpNavDropdown>
            {hasAccess([Permissions.ReadDataGraph]) && (
              <AwpNavDropdown {...props} title="Graphs" id="graphs-dropdown">
                {EnabledFeature.SingleStationGraph && (
                  <AwpDropdownMenuItem
                    {...props}
                    to={Views.SingleStationGraph}
                    title="Single Station"
                    permissions={[Permissions.ReadDataGraph]}
                    onClick={() => expandedClick()}
                  />
                )}
                {EnabledFeature.MultiWeatherStation && (
                  <AwpDropdownMenuItem
                    {...props}
                    to={Views.MultiStationGraph}
                    title="Multi Station"
                    permissions={[Permissions.ReadDataGraph]}
                    onClick={() => expandedClick()}
                  />
                )}
                {EnabledFeature.FrostProbe && (
                  <AwpDropdownMenuItem
                    {...props}
                    to={Views.FrostProbe}
                    title="Frost Probe"
                    permissions={[Permissions.ReadDataGraph]}
                    onClick={() => expandedClick()}
                  />
                )}
                {EnabledFeature.MultiStationPresets && (
                  <AwpDropdownMenuItem
                    {...props}
                    to={Views.GraphPresets}
                    title="Presets"
                    permissions={[Permissions.ReadDataGraph]}
                    onClick={() => expandedClick()}
                  />
                )}
              </AwpNavDropdown>
            )}
            <AwpMenuItem
              {...props}
              to={Views.DataTable}
              title="Tables"
              permissions={[Permissions.ReadDataTable]}
              onClick={() => expandedClick()}
            />
            <AwpMenuItem
              {...props}
              to={Views.Maps}
              title="Maps"
              permissions={[Permissions.ReadMaps]}
              onClick={() => expandedClick()}
            />

            {hasAccess([Permissions.ReadInventory]) && (
              <AwpNavDropdown {...props} title="Inventory" id="navigation-dropdown">
                <AwpDropdownMenuItem
                  {...props}
                  to={Views.WeatherStation}
                  title="Weather Station"
                  permissions={[Permissions.ReadInventory]}
                  onClick={() => expandedClick()}
                />
                <AwpDropdownMenuItem
                  {...props}
                  to={Views.SensorEquipment}
                  title="Sensor Equipment"
                  permissions={[Permissions.ReadInventory]}
                  onClick={() => expandedClick()}
                />
                <AwpDropdownMenuItem
                  {...props}
                  to={Views.SensorModel}
                  title="Sensor Models"
                  permissions={[Permissions.ReadInventory]}
                  onClick={() => expandedClick()}
                />
                <AwpDropdownMenuItem
                  {...props}
                  to={Views.SensorType}
                  title="Sensor Types"
                  permissions={[Permissions.ReadInventory]}
                  onClick={() => expandedClick()}
                />
                <AwpDropdownMenuItem
                  {...props}
                  to={Views.ObservationType}
                  title="Observation Types"
                  permissions={[Permissions.ReadInventory]}
                  onClick={() => expandedClick()}
                />
                <AwpDropdownMenuItem
                  {...props}
                  to={Views.Manufacturer}
                  title="Manufacturers"
                  permissions={[Permissions.ReadInventory]}
                  onClick={() => expandedClick()}
                />
                <AwpDropdownMenuItem
                  {...props}
                  to={Views.SoilType}
                  title="Soil Types"
                  permissions={[Permissions.ReadInventory]}
                  onClick={() => expandedClick()}
                />
              </AwpNavDropdown>
            )}

            {hasAccess([Permissions.ReadAvalanceForm, Permissions.ReadAvalancheHazard]) && (
              <AwpNavDropdown {...props} title="Avalanche" id="navigation-dropdown" supportOffline={true}>
                <Dropdown.Header className="text-dark font-weight-bold">Pending Events</Dropdown.Header>
                <AwpDropdownMenuItem
                  to={Views.Avalanche}
                  state={{
                    mode: FORM_MODE.LIST,
                  }}
                  title="Avalanche Events List"
                  permissions={[Permissions.ReadAvalanceForm]}
                  supportOffline={true}
                  onClick={() => expandedClick()}
                />
                <AwpDropdownMenuItem
                  to={Views.Avalanche}
                  state={{
                    mode: FORM_MODE.CREATE,
                    eventType: EventTypes.AvalancheOccurrence,
                    fromMenu: true,
                  }}
                  title="Avalanche Observations"
                  permissions={[Permissions.WriteAvalancheForm]}
                  supportOffline={true}
                  onClick={() => expandedClick()}
                />
                <AwpDropdownMenuItem
                  to={Views.Avalanche}
                  state={{
                    mode: FORM_MODE.CREATE,
                    eventType: EventTypes.RoadClosure,
                    fromMenu: true,
                  }}
                  title="Road Closure"
                  permissions={[Permissions.WriteAvalancheForm]}
                  supportOffline={true}
                  onClick={() => expandedClick()}
                />
                <AwpDropdownMenuItem
                  to={Views.Avalanche}
                  state={{
                    mode: FORM_MODE.CREATE,
                    eventType: EventTypes.AvalancheControl,
                    fromMenu: true,
                  }}
                  title="Avalanche Control"
                  permissions={[Permissions.WriteAvalancheForm]}
                  supportOffline={true}
                  onClick={() => expandedClick()}
                />

                <SupportOffline supportOffline={false}>
                  <Dropdown.Header className="text-dark font-weight-bold">Processed Events</Dropdown.Header>
                </SupportOffline>
                <AwpDropdownMenuItem
                  to={Views.AvalancheProcessedEvent}
                  state={{
                    mode: FORM_MODE.LIST,
                  }}
                  title="Processed Events List"
                  permissions={[Permissions.ReadAvalanceForm, Permissions.ReadAvalancheHazard]}
                  supportOffline={false}
                  onClick={() => expandedClick()}
                />
                <AwpDropdownMenuItem
                  to={Views.AvalancheProcessedEvent}
                  state={{
                    mode: FORM_MODE.CREATE,
                    eventType: EventTypes.HazardForecast,
                  }}
                  title="Hazard Forecast"
                  permissions={[Permissions.WriteAvalancheHazard]}
                  supportOffline={false}
                  onClick={() => expandedClick()}
                />

                <SupportOffline supportOffline={false}>
                  <Dropdown.Header className="text-dark font-weight-bold">Other Functions</Dropdown.Header>
                </SupportOffline>
                <AwpDropdownMenuItem
                  to={Views.AvalancheMonitor}
                  title="Avalanche Monitor"
                  permissions={[Permissions.ReadAvalanceForm]}
                  onClick={() => expandedClick()}
                  {...props}
                />
                <AwpDropdownMenuItem
                  {...props}
                  to={Views.ForecastRecipient}
                  title="Hazard Forecast Contact List"
                  permissions={[Permissions.ReadAvalanceForm]}
                  onClick={() => expandedClick()}
                />
              </AwpNavDropdown>
            )}
            {hasAccess([Permissions.ReadAdminTools, Permissions.AdminDefault]) && (
              <AwpNavDropdown {...props} title="Admin Tools" id="navigation-dropdown">
                <AwpDropdownMenuItem
                  {...props}
                  to={Views.IngestionFailure}
                  title="Data Ingestion Issue Log"
                  permissions={[Permissions.ReadAdminTools]}
                  onClick={() => expandedClick()}
                />
                <AwpDropdownMenuItem
                  {...props}
                  to={Views.Dashboard}
                  title="Monitoring"
                  permissions={[Permissions.ReadAdminTools]}
                  onClick={() => expandedClick()}
                />
                <AwpDropdownMenuItem
                  {...props}
                  to={Views.AccessRequest}
                  title="Access Requests"
                  permissions={[Permissions.AdminDefault]}
                  onClick={() => expandedClick()}
                />
                <AwpDropdownMenuItem
                  {...props}
                  to={Views.User}
                  title="Registered Users"
                  permissions={[Permissions.AdminDefault]}
                  onClick={() => expandedClick()}
                />
                <AwpDropdownMenuItem
                  {...props}
                  to={Views.Role}
                  title="List User Roles"
                  permissions={[Permissions.AdminDefault]}
                  onClick={() => expandedClick()}
                />
                <AwpDropdownMenuItem
                  {...props}
                  to={Views.ManualDataValidation}
                  title="Manual Data Validation"
                  permissions={[Permissions.ReadAdminTools]}
                  onClick={() => expandedClick()}
                />
                <AwpDropdownMenuItem
                  {...props}
                  to={Views.AvalancheAreas}
                  title="Avalanche Areas"
                  permissions={[Permissions.ReadAdminTools]}
                  onClick={() => expandedClick()}
                />
                <AwpDropdownMenuItem
                  {...props}
                  to={Views.RoadClosureLocations}
                  title="Road Closure Locations"
                  permissions={[Permissions.ReadAdminTools]}
                  onClick={() => expandedClick()}
                />
                <AwpDropdownMenuItem
                  {...props}
                  to={Views.RoadClosureAreas}
                  title="Road Closure Areas"
                  permissions={[Permissions.ReadAdminTools]}
                  onClick={() => expandedClick()}
                />
                {EnabledFeature.AvalanchePaths && (
                  <AwpDropdownMenuItem
                    {...props}
                    to={Views.AvalanchePaths}
                    title="Avalanche Paths"
                    permissions={[Permissions.ReadAdminTools]}
                    onClick={() => expandedClick()}
                  />
                )}
                <AwpDropdownMenuItem
                  {...props}
                  to={Views.StandardGraphAxis}
                  title="Standard Graph Axis"
                  permissions={[Permissions.ReadInventory]}
                  onClick={() => expandedClick()}
                />
                <AwpDropdownMenuItem
                  {...props}
                  to={Views.AvalanchePrograms}
                  title={ENTITY_TITLES.AVALANCHE_PROGRAMS}
                  permissions={[Permissions.ReadAdminTools]}
                  onClick={() => expandedClick()}
                />
                {EnabledFeature.HazardForecastArea && (
                  <AwpDropdownMenuItem
                    {...props}
                    to={Views.HazardForecastArea}
                    title={ENTITY_TITLES.HAZARD_FORECAST_AREAS}
                    permissions={[Permissions.ReadAdminTools]}
                    onClick={() => expandedClick()}
                  />
                )}
                <AwpDropdownMenuItem
                  {...props}
                  to={Views.HazardForecastTemplate}
                  title={ENTITY_TITLES.HAZARD_FORECAST_TEMPLATES}
                  permissions={[Permissions.ReadAdminTools]}
                  onClick={() => expandedClick()}
                />
              </AwpNavDropdown>
            )}
            <AwpMenuItem
              {...props}
              to={Views.Notification}
              title="Alerts"
              permissions={[Permissions.ReadAlertDefinition]}
              supportOffline={false}
              onClick={() => expandedClick()}
            />
            {hasAccess([Permissions.ReadVms, Permissions.WriteVms]) && (
              <AwpNavDropdown {...props} title="VMS" id="navigation-dropdown">
                {EnabledFeature.VMS && (
                  <AwpDropdownMenuItem
                    {...props}
                    to={Views.VmsControl}
                    title="Control Variable Message Signs"
                    permissions={[Permissions.WriteVmsControl]}
                    onClick={() => expandedClick()}
                  />
                )}
                <AwpDropdownMenuItem
                  {...props}
                  to={Views.Vms}
                  title="List Variable Message Signs"
                  permissions={[Permissions.ReadVms]}
                  onClick={() => expandedClick()}
                />
              </AwpNavDropdown>
            )}
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

export default HeaderMenu;
